// DerivedDataSetの評価関数

import type { DerivedDataSet, DataSet, DataSchema } from '../../types/dataset.types';
import { executeOperation, type OperationResult } from './executeOperation';
import logger from './logger';

/**
 * 2つのスキーマが互換性があるかチェック
 * DuckDB統合により、カラムIDは動的に生成されるため、
 * カラム名と型のみで比較する
 * @param schema1 比較対象のスキーマ1
 * @param schema2 比較対象のスキーマ2
 * @returns スキーマが互換性がある場合true
 */
function isSchemaCompatible(schema1: DataSchema, schema2: DataSchema): boolean {
  // カラム数チェック
  if (schema1.columns.length !== schema2.columns.length) {
    return false;
  }

  // 各カラムの互換性をチェック（名前と型のみ）
  for (let i = 0; i < schema1.columns.length; i++) {
    const col1 = schema1.columns[i];
    const col2 = schema2.columns[i];

    if (col1.name !== col2.name ||
        col1.type !== col2.type) {
      return false;
    }
  }

  return true;
}

/**
 * DerivedDataSetを評価して結果を返す（Public版、SQL操作時はDuckDB接続ロックを取得する）
 *
 * **ロックについて**：
 * SQL操作（sql_query）の場合、内部でexecuteOperation → executeSQL → withLock()の順で呼ばれ、
 * DuckDB-WASMの接続（conn）への排他アクセスを管理するロックを取得する。
 * Filter、Crosstabなどのロック不要な操作の場合はロックを取得しない。
 *
 * **前提条件**：
 * - 親データセットが既に評価済みであること（parentDataset.data != null）
 *   親が未評価の場合はassertionでエラーとなる
 *
 * **処理フロー**：
 * 1. データがキャッシュされている場合はそれを返す（早期リターン）
 * 2. 親データセットの存在と評価済み状態を確認する（assertion）
 * 3. executeOperation（Public版）を呼び出して操作を実行する
 *    - SQL操作の場合、内部でロックが取得される
 *    - その他の操作の場合、ロック取得なし
 *
 * **使用場面**：
 * - DerivedDataAccessor.loadData()から呼ばれる場合（タブが開かれた時など）
 * - ユーザーが明示的にDerivedDataSetを評価する場合
 *
 * **注意**：
 * Worker内のトポロジカル評価（ensureRequiredDatasetsRegisteredByTableNames）では、
 * executeOperationWithoutLockを直接使用すること。この関数はメインスレッド用。
 *
 * @param derivedDataset 評価対象のDerivedDataSet
 * @param datasets 利用可能なデータセットのマップ
 * @returns 評価結果
 */
export async function evaluateDerivedDataset(
  derivedDataset: DerivedDataSet,
  datasets: Record<string, DataSet>
): Promise<OperationResult> {
  // すでにキャッシュされたデータがある場合はそれを返す
  if (derivedDataset.data) {
    return {
      schema: derivedDataset.schema,
      data: derivedDataset.data,
      rowCount: derivedDataset.rowCount || 0,
      parentDatasetIds: derivedDataset.parentDatasetIds
    };
  }

  // 操作が定義されていない場合はエラー
  if (!derivedDataset.operation) {
    throw new Error('No operation defined for derived dataset');
  }

  const parentDatasetId = derivedDataset.parentDatasetIds[0];

  // 親データセットの存在確認
  const parentDataset = datasets[parentDatasetId];
  logger.assert(parentDataset != null,
    `Parent dataset ${parentDatasetId} must exist for derived dataset ${derivedDataset.name}`);

  // 親データセットが既に評価済みであることを確認
  logger.assert(parentDataset.data != null,
    `Parent dataset ${parentDataset.name} must be evaluated before evaluating derived dataset ${derivedDataset.name}`);

  // 操作を実行（Public版を使用、内部でロック取得、derivedDatasetを渡してexclusionsを適用）
  const result = await executeOperation(
    derivedDataset.operation,
    datasets,
    parentDatasetId,
    derivedDataset.schema,
    derivedDataset
  );

  // スキーマ互換性チェック
  if (!isSchemaCompatible(result.schema, derivedDataset.schema)) {
    logger.warn('Schema structure changed:', {
      expected: derivedDataset.schema.columns.map(c => ({ name: c.name, type: c.type })),
      actual: result.schema.columns.map(c => ({ name: c.name, type: c.type })),
      rowCountChange: { from: derivedDataset.rowCount || 0, to: result.rowCount }
    });
  }

  return result;
}